import {
  useState,
  useRef,
  useEffect,
  useId,
  type FocusEvent as ReactFocusEvent,
  type JSX,
} from 'react';
import { placeDropdown, type DOMRectLike, type DropdownPlacement } from '@cca/ui';
import type { ParishSummary } from '../api-client';

interface ParishPickerProps {
  parishes: ParishSummary[];
  activeParishId: string | null;
  onChange: (id: string) => void;
}

/** The parish list's preferred width; never wider than the screen less two edges. */
export const LISTBOX_WIDTH = 280;
/** The clear space kept between the open list and either screen edge. */
export const LISTBOX_EDGE = 16;

/**
 * Where the open parish list goes so it is ALWAYS fully on screen (cca-69gs,
 * WCAG 2.2 SC 1.4.10): `@cca/ui`'s shared `placeDropdown`, LEFT-first — room to
 * the right of the picker hangs it from the picker's left edge, otherwise from
 * its right edge, otherwise pushed in {@link LISTBOX_EDGE} from the left of the
 * screen. The placement logic and its sweep tests live in `@cca/ui` (cca-5weu),
 * shared with the Sign out menu, which prefers its right edge.
 */
export function placeParishList(picker: DOMRectLike, vw: number): DropdownPlacement {
  return placeDropdown(picker, vw, { width: LISTBOX_WIDTH, edge: LISTBOX_EDGE, prefer: 'left' });
}

/** The visible viewport width, less any vertical scrollbar. */
function viewportWidth(): number {
  return document.documentElement.clientWidth;
}

/** The current-parish switcher in the header. Styled with the shared `.btn-header`
 *  pill so it reads as one family with the Admin and Sign-out controls beside it
 *  (the only difference being the status dot + dropdown chevron).
 *
 *  A DISCLOSURE, like @cca/ui's SignOutMenu (WCAG 2.2 SC 2.4.3 + 4.1.2, cca-5weu):
 *  the trigger carries `aria-expanded` and, while open, `aria-controls` naming the
 *  list; each parish is a plain button reached with Tab in DOM order, the current
 *  one marked `aria-current`. No listbox/option roles — those promise arrow-key
 *  selection this does not provide. Escape (from the trigger or the list) and
 *  choosing a parish close it and return focus to the trigger; a click elsewhere
 *  closes it without taking focus, and so does focus LEAVING it — Tab past the
 *  last parish, or Shift+Tab out past the trigger (SC 2.4.11) — so the open list
 *  never covers whatever focus moved on to. Moving between the trigger and the
 *  parishes keeps it open.
 *
 *  The trigger's accessible name is "Parish: <name>" (SC 2.4.6 / 2.5.3): a
 *  visually-hidden prefix says what the control is, and the name still contains
 *  the visible label, so a voice user can say what they see. */
export function ParishPicker({
  parishes,
  activeParishId,
  onChange,
}: ParishPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  // Measure the picker against the viewport (clientWidth: the visible width,
  // less any scrollbar) — when the list opens, and again if the window resizes
  // while it is open (a phone rotating).
  const measure = (): void => {
    const el = ref.current;
    if (el) setPlacement(placeParishList(el.getBoundingClientRect(), viewportWidth()));
  };

  // Focus moving OUT of the picker closes the list — the click-elsewhere rule, and
  // like it, focus stays where it went. Between the trigger and the parishes it is
  // still inside, so the list stays open. A null relatedTarget (the window losing
  // focus, a click on a non-focusable spot, or Safari's click on a button, which
  // never focuses it) does NOT close it: closing there would unmount a parish
  // between its mousedown and its click.
  const closeOnFocusLeave = (e: ReactFocusEvent<HTMLDivElement>): void => {
    const next = e.relatedTarget;
    if (next !== null && !e.currentTarget.contains(next)) setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    function handleResize() {
      const el = ref.current;
      if (el) setPlacement(placeParishList(el.getBoundingClientRect(), viewportWidth()));
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  if (parishes.length === 0) {
    return (
      // Solid white, like the header text beside it: on the parishes header
      // (#86754d) even full white is only 4.50:1, so any alpha below 1 fails AA.
      <span style={{ fontSize: 12, color: '#fff' }}>No parishes assigned</span>
    );
  }

  const active = parishes.find((p) => p.id === activeParishId) ?? parishes[0];
  const dot = (color: string): JSX.Element => (
    <span
      style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }}
      aria-hidden="true"
    />
  );

  // A single parish is a static label — the same header-pill look, minus the
  // dropdown affordance. On a narrow screen a long parish name WRAPS inside the
  // pill (never an ellipsis), capped at the header slot's width (cca-69gs).
  if (parishes.length === 1) {
    return (
      <span
        className="btn-header"
        style={{ cursor: 'default', maxWidth: '100%', whiteSpace: 'normal' }}
      >
        {dot('#fff')}
        {active?.name}
      </span>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: '100%' }} onBlur={closeOnFocusLeave}>
      <button
        ref={triggerRef}
        type="button"
        className={`btn-header${open ? ' is-open' : ''}`}
        style={{ maxWidth: '100%', whiteSpace: 'normal' }}
        aria-expanded={open}
        // Names the list only while it exists — a reference to an absent id is
        // an invalid IDREF.
        aria-controls={open ? listId : undefined}
        onClick={() => {
          if (!open) measure();
          setOpen(!open);
        }}
      >
        {dot('#fff')}
        {/* Heard, not seen: the name reads "Parish: St Ives". `sr-only` is
            Tailwind's visually-hidden utility, already in this portal's build. */}
        <span className="sr-only">{'Parish: '}</span>
        {active?.name}
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          style={{
            flexShrink: 0,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          id={listId}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            // Placed by placeParishList() so it is always on screen; before the
            // first measurement (never, in a browser) the old right-hung default.
            ...(placement
              ? { left: placement.left, width: placement.width }
              : { right: 0, width: LISTBOX_WIDTH }),
            maxWidth: `calc(100vw - ${2 * LISTBOX_EDGE}px)`,
            // The width INCLUDES the border and padding, or the placement's
            // arithmetic would be 10px short at the screen edge.
            boxSizing: 'border-box',
            background: 'var(--color-panel, #fff)',
            border: '1px solid var(--color-border, #e5e0d5)',
            borderRadius: 'var(--radius-sm, 8px)',
            boxShadow: 'var(--shadow-pop, 0 8px 30px rgba(0, 0, 0, 0.18))',
            zIndex: 100,
            padding: 4,
            // Registrar sees every parish — cap height so a long list scrolls
            // instead of running off-screen.
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {parishes.map((p) => {
            const isActive = p.id === activeParishId;
            return (
              <button
                key={p.id}
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '9px 12px',
                  background: isActive ? 'var(--color-structural-wash, #f5f0e8)' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-xs, 4px)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive
                    ? 'var(--color-structural-dk, #5c4f33)'
                    : 'var(--color-ink, #111827)',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--color-surface, #f6f3ec)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {dot(isActive ? 'var(--color-accent, #86754d)' : 'var(--color-border, #d8d2c4)')}
                {p.name}
                {isActive && (
                  <svg
                    style={{ marginLeft: 'auto', flexShrink: 0 }}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 7L5.5 10L11.5 4"
                      stroke="var(--color-accent, #86754d)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
